// src/ConfirmActionModal.js
import React, { useState, useEffect } from "react";
import "./ConfirmActionModal.css";

const ConfirmActionModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmButtonText = "Confirm",
  cancelButtonText = "Cancel",
  isDestructive = false,
  isProcessing = false,
  requireTypedConfirmation = false,
  confirmationWord = "delete",
  caseSensitive = true,
  details = null,
  warningText = null,
}) => {
  const [typedText, setTypedText] = useState("");

  // Reset typed text only when the modal opens/closes — details is presentational
  // and must not be a dependency or background re-renders will clear the input mid-typing
  useEffect(() => {
    if (isOpen) {
      setTypedText("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (requireTypedConfirmation) {
      const isMatch = caseSensitive
        ? typedText === confirmationWord
        : typedText.toLowerCase() === confirmationWord.toLowerCase();
      if (!isMatch) {
        return;
      }
    }
    onConfirm?.();
  };

  const handleClose = () => {
    setTypedText("");
    onClose();
  };

  const isConfirmDisabled = () => {
    if (isProcessing) return true;
    if (requireTypedConfirmation) {
      const isMatch = caseSensitive
        ? typedText === confirmationWord
        : typedText.toLowerCase() === confirmationWord.toLowerCase();
      return !isMatch;
    }
    return false;
  };

  return (
    <>
      <div className="confirm-modal-overlay" onClick={handleClose} />
      <div className="confirm-modal">
        <h2 className={isDestructive ? "confirm-modal-title-destructive" : ""}>
          {title}
        </h2>
        <div className="confirm-modal-content">
          <p className="confirm-modal-message">{message}</p>

          {details && <div className="confirm-modal-details">{details}</div>}

          {warningText && (
            <div className="confirm-modal-warning">{warningText}</div>
          )}

          {requireTypedConfirmation && (
            <div className="confirm-modal-typed-confirmation">
              <p className="typed-confirmation-instruction">
                Type <strong>{confirmationWord}</strong> to confirm:
              </p>
              <input
                type="text"
                className="typed-confirmation-input"
                value={typedText}
                onChange={(e) => setTypedText(e.target.value)}
                placeholder={`Type "${confirmationWord}"`}
                autoFocus
                disabled={isProcessing}
              />
            </div>
          )}
        </div>

        <div className="confirm-modal-actions">
          {cancelButtonText && (
            <button
              className="confirm-modal-cancel-btn"
              onClick={handleClose}
              disabled={isProcessing}
            >
              {cancelButtonText}
            </button>
          )}
          {confirmButtonText && (
            <button
              className={`confirm-modal-confirm-btn ${
                isDestructive ? "destructive" : ""
              }`}
              onClick={handleConfirm}
              disabled={isConfirmDisabled()}
            >
              {isProcessing ? "Processing..." : confirmButtonText}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default ConfirmActionModal;
